/**
 * RedditTemplate.tsx — Reddit Lookup. Covers: combined_id when a row carries
 * an AI-generated `report` object (`report.profile_assessment` — see
 * registry.ts's `looksLikeRedditReport`, which routes on this shape rather
 * than on `sources[]` naming, since the OLD UI's own routing missed this
 * entirely — RedditResultsViewer.tsx (src/container/searcher/
 * result_components/) is mounted from NameCommon.tsx by a plain
 * `item.source === 'reddit'` filter, a workflow this app's own
 * `irbispro-v2/config/sources.ts` documents as also sending `'redditid'`).
 *
 * Unlike insta_story's counters-and-media shape, Reddit's payload here is an
 * AI report, not raw profile fields: no karma, no account-creation date, no
 * subreddit list, no structured post/comment bodies — those only exist as
 * unstructured prose inside `raw_source_summary`/`evidence[].quote_or_excerpt`
 * (confirmed independently by the already-migrated
 * `irbispro-v2/adapters/profileReport.ts`, which has to regex them back out).
 * Every field below is read from that report shape as-is, no fields
 * invented. Field paths, cross-checked against RedditResultsViewer.tsx:
 *
 *   report.username, .status, .created_at, .completed_at
 *   report.raw_source_summary: string[], .limitations: string[]
 *   report.collection_summary: { analysis_depth, collection_status,
 *     reddit_profile_collected, reddit_posts_count, reddit_comments_count,
 *     external_mentions_count }
 *   report.evidence[]: { source_url, source_type, confidence_impact,
 *     quote_or_excerpt, inference_type, created_utc }
 *   report.profile_assessment: {
 *     possible_location: { city, region, country }
 *     gender_indicators.value, age_range.value
 *     professional_indicators: { confidence, possible_roles[],
 *       role_self_claims[], possible_products[] (each may be a JSON-string
 *       — old UI JSON.parses it — with product_name/confidence/evidence[0]) }
 *     communication_profile: { tone, style, main_topics[] }
 *     linked_usernames[]
 *     cross_platform_matches[]: { platform, username, confidence_label,
 *       match_confidence_score, assessment, url }
 *   }
 *
 * The old UI also live-geocoded `possible_location` client-side (Nominatim
 * fetch + its own Google Maps mount) just to plot one pin. Reusing the
 * shared `MapCard` here instead — it already highlights a country from free
 * text via word-boundary name matching, no network geocoding call needed,
 * and it's the same map every other template uses.
 */
import * as React from 'react'
import { Box, Typography } from '@mui/material'
import {
  Chips, Cols, FieldTable, Flag, FlagTone, NoData, Photo, RawCard, ResCard, Sech, sentence, ShowMoreButton, SourceBadge,
  Stack, Stats, usePhotoLightbox, useShowMore, useTokens, ValueList, ValueRow,
} from '../ui/primitives'
import { hasMapPins, MapCard } from '../ui/MapCard'
import { rowsOf } from '../registry'
import type { TemplateProps } from '../registry'

/**
 * Same masonry technique PersonTemplate.tsx uses for its own Map card: a
 * fixed per-tile height so the pinned 2-column × 2-row Map block (see the
 * `midCards` grid below) lines up exactly with two auto-flowed 1-row cards
 * stacked beside it, plus the `Cols` gap between them.
 */
const MID_TILE_HEIGHT = 250
const MID_COLS_GAP = 14

type EvidenceItem = {
  sourceUrl?: string
  sourceType?: string
  confidenceImpact?: string
  quote?: string
  inferenceType?: string
  createdUtc?: number
}

type ProductItem = { name?: string; confidence?: string; quote?: string; sourceUrl?: string }

type CrossPlatformItem = {
  platform?: string
  username?: string
  confidenceLabel?: string
  matchScore?: number
  assessment?: string
  url?: string
}

type RedditReport = {
  username?: string
  status?: string
  createdAt?: string
  completedAt?: string
  analysisDepth?: string
  collectionStatus?: string
  profileCollected?: boolean
  postsCount?: number
  commentsCount?: number
  externalMentionsCount?: number
  rawSourceSummary: string[]
  limitations: string[]
  evidence: EvidenceItem[]
  location: { city?: string; region?: string; country?: string }
  gender?: string
  ageRange?: string
  profConfidence?: string
  possibleRoles: string[]
  roleSelfClaims: string[]
  possibleProducts: ProductItem[]
  tone?: string
  style?: string
  topics: string[]
  linkedUsernames: string[]
  crossPlatform: CrossPlatformItem[]
}

function tryParseJson(s: string): any {
  try {
    return JSON.parse(s)
  } catch {
    return null
  }
}

function normalizeProducts(raw: any[]): ProductItem[] {
  return raw.map((p) => {
    const parsed = typeof p === 'string' ? tryParseJson(p) : p
    if (!parsed || typeof parsed !== 'object') {
      return { name: typeof p === 'string' ? p : undefined }
    }
    const ev0 = Array.isArray(parsed.evidence) ? parsed.evidence[0] : undefined
    return {
      name: parsed.product_name,
      confidence: parsed.confidence,
      quote: ev0?.quote_or_excerpt,
      sourceUrl: ev0?.source_url,
    }
  })
}

/**
 * Adapter for an alternate feed shape seen from the scraping pipeline: a
 * `ProfileItem` row whose report lives at `row.profileData.report` instead
 * of directly on `row.report`. Its siblings in the same `data[]` array are
 * raw `PostItem` posts with no `report` at all — left untouched here,
 * they're read separately by `extractRedditPosts` below and still appear
 * in the `RawCard` dump since that renders the untouched apiData.
 *
 * Handles apiData either already wrapped as `{ apiDataEntities: [...] }`
 * (the normal SearchResult shape — the nested `profileData.report` can
 * show up on a row inside it too, not just in a bare array) or as a plain
 * array of `{ data: [...] }` containers with no wrapper at all. Rows that
 * already carry `.report` directly pass through unchanged.
 *
 * Reads rows through `rowsOf` (from registry.ts) rather than a bare
 * `.data` — some sources (`irbis-social-tiktok` and siblings) wrap
 * `entity.data` one level deeper as a single-item envelope
 * `{ ok, source, totalItems, data: [...] }`; `rowsOf` unwraps that, and
 * using the same helper the router used to pick this template in the
 * first place means the row shape here always matches what routing saw.
 */
function normalizeApiData(apiData: any): any {
  const containers: any[] = Array.isArray(apiData?.apiDataEntities)
    ? apiData.apiDataEntities
    : Array.isArray(apiData)
      ? apiData
      : [apiData]

  const entities = containers.map((c: any) => {
    const rows: any[] = rowsOf(c)
    if (rows.length === 0) return c
    const patchedRows = rows.map((r: any) => {
      if (r && typeof r === 'object' && !r.report && r.profileData && typeof r.profileData === 'object' && r.profileData.report) {
        return { ...r, report: r.profileData.report }
      }
      return r
    })
    return { ...c, data: patchedRows }
  })

  return { apiDataEntities: entities }
}

type RedditAccountMeta = {
  username?: string
  displayName?: string
  createdUtc?: number
  commentKarma?: number
  linkKarma?: number
  totalKarma?: number
  isEmployee?: boolean
  verified?: boolean
  description?: string
  iconImg?: string
}

/**
 * `ProfileItem.profileData.collection.profile` — the raw Reddit account
 * record from the scrape itself (karma, account-creation date, verified
 * badge, avatar), distinct from the AI-generated `report` read by
 * `extract()` above. Same row-gathering as `extractRedditPosts`: goes
 * through `rowsOf` so envelope-wrapped `entity.data` unwraps the same way
 * routing already unwrapped it.
 */
function extractRedditAccountMeta(apiDataRaw: any): RedditAccountMeta | null {
  const rows: any[] = []

  const containers: any[] = Array.isArray(apiDataRaw) ? apiDataRaw : [apiDataRaw]
  for (const c of containers) rows.push(...rowsOf(c))

  const entities: any[] = Array.isArray(apiDataRaw?.apiDataEntities) ? apiDataRaw.apiDataEntities : []
  for (const e of entities) rows.push(...rowsOf(e))

  const row = rows.find((r: any) => r?.type === 'ProfileItem' && r?.profileData?.collection?.profile)
  const p = row?.profileData?.collection?.profile
  if (!p || typeof p !== 'object') return null

  return {
    username: p.username || undefined,
    displayName: p.display_name || undefined,
    createdUtc: typeof p.created_utc === 'number' ? p.created_utc : undefined,
    commentKarma: typeof p.comment_karma === 'number' ? p.comment_karma : undefined,
    linkKarma: typeof p.link_karma === 'number' ? p.link_karma : undefined,
    totalKarma: typeof p.total_karma === 'number' ? p.total_karma : undefined,
    isEmployee: !!p.is_employee,
    verified: !!p.verified,
    description: p.profile_description || undefined,
    iconImg: p.icon_img || undefined,
  }
}

type RedditPostComment = { author?: string; text?: string; url?: string; score?: number }

type RedditPost = {
  id: string
  title?: string
  text?: string
  url?: string
  postUrl?: string
  createdUtc?: number
  score?: number
  numComments?: number
  subreddit?: string
  images: string[]
  comments: RedditPostComment[]
}

/**
 * `PostItem` rows — raw Reddit posts, siblings of the `ProfileItem` in the
 * same `data[]` array (see `normalizeApiData` above for why they aren't
 * part of the AI report and are collected separately here). Supports both
 * the plain-array wrapper (`[{ data: [...] }]`) and the older
 * `apiDataEntities[].data[]` shape, in case a `PostItem` ever shows up
 * there too.
 *
 * Field shape confirmed against a real payload: `media.images[]` is an
 * array of `{ url, width, height, source }` objects (not plain strings),
 * and `user_comments.items[]` is `{ id, body, permalink, subreddit,
 * created_utc, score, parent_id, link_id }` — the comment text is `body`,
 * not `text`.
 */
function extractRedditPosts(apiDataRaw: any): RedditPost[] {
  const rows: any[] = []

  const containers: any[] = Array.isArray(apiDataRaw) ? apiDataRaw : [apiDataRaw]
  for (const c of containers) rows.push(...rowsOf(c))

  const entities: any[] = Array.isArray(apiDataRaw?.apiDataEntities) ? apiDataRaw.apiDataEntities : []
  for (const e of entities) rows.push(...rowsOf(e))

  return rows
    .filter((r: any) => r?.type === 'PostItem')
    .map((r: any, idx: number): RedditPost => {
      const rawImages = Array.isArray(r?.media?.images) ? r.media.images : []
      const images: string[] = rawImages
        // Each entry can carry multiple sizes/variants of the same image
        // (`source: "post_url"` vs `"preview"`, sometimes with null
        // width/height on the preview one) — only "post_url" is the actual
        // post image, so drop the rest to avoid duplicate tiles. Plain
        // strings or objects with no `source` field (other shapes) still
        // pass through unfiltered.
        .filter((im: any) => typeof im === 'string' || !im?.source || im.source === 'post_url')
        .map((im: any) => (typeof im === 'string' ? im : im?.url || im?.src || im?.image_url))
        .filter((v: any): v is string => typeof v === 'string' && v.length > 0)

      const rawComments = Array.isArray(r?.user_comments?.items) ? r.user_comments.items : []
      const comments: RedditPostComment[] = rawComments
        .map((c: any) => ({
          author: c?.author || c?.user || c?.username || undefined,
          text: c?.body || c?.text || c?.comment || undefined,
          url: c?.permalink || undefined,
          score: typeof c?.score === 'number' ? c.score : undefined,
        }))
        .filter((c: RedditPostComment) => !!c.text)

      return {
        id: r?.post_id || r?.post_url || String(idx),
        title: r?.title || undefined,
        text: r?.selftext || r?.text || undefined,
        url: r?.url || undefined,
        postUrl: r?.post_url || r?.posted_by_profile_url || undefined,
        createdUtc: typeof r?.created_utc === 'number' ? r.created_utc : typeof r?.timeStamp === 'number' ? r.timeStamp : undefined,
        score: typeof r?.score === 'number' ? r.score : undefined,
        numComments: typeof r?.num_comments === 'number' ? r.num_comments : undefined,
        subreddit: r?.subreddit || undefined,
        images,
        comments,
      }
    })
}

function extract(apiDataRaw: any): RedditReport | null {
  const apiData = normalizeApiData(apiDataRaw)
  const entities: any[] = Array.isArray(apiData?.apiDataEntities) ? apiData.apiDataEntities : []
  for (const e of entities) {
    const rows = Array.isArray(e?.data) ? e.data : []
    for (const r of rows) {
      const report = r?.report
      if (!report || typeof report !== 'object') continue

      const summary = report.collection_summary || {}
      const profile = report.profile_assessment || {}
      const prof = profile.professional_indicators || {}
      const comm = profile.communication_profile || {}
      const loc = profile.possible_location || {}

      const evidence: EvidenceItem[] = (Array.isArray(report.evidence) ? report.evidence : []).map((ev: any) => ({
        sourceUrl: ev?.source_url || undefined,
        sourceType: ev?.source_type || undefined,
        confidenceImpact: ev?.confidence_impact || undefined,
        quote: ev?.quote_or_excerpt || undefined,
        inferenceType: ev?.inference_type || undefined,
        createdUtc: typeof ev?.created_utc === 'number' ? ev.created_utc : undefined,
      }))

      const crossPlatform: CrossPlatformItem[] = (Array.isArray(profile.cross_platform_matches) ? profile.cross_platform_matches : []).map(
        (c: any) => ({
          platform: c?.platform || undefined,
          username: c?.username || undefined,
          confidenceLabel: c?.confidence_label || undefined,
          matchScore: typeof c?.match_confidence_score === 'number' ? c.match_confidence_score : undefined,
          assessment: c?.assessment || undefined,
          url: c?.url || undefined,
        }),
      )

      return {
        username: report.username || r?.profile_id || undefined,
        status: report.status || undefined,
        createdAt: report.created_at || undefined,
        completedAt: report.completed_at || undefined,
        analysisDepth: summary.analysis_depth || undefined,
        collectionStatus: summary.collection_status || undefined,
        profileCollected: !!summary.reddit_profile_collected,
        postsCount: typeof summary.reddit_posts_count === 'number' ? summary.reddit_posts_count : undefined,
        commentsCount: typeof summary.reddit_comments_count === 'number' ? summary.reddit_comments_count : undefined,
        externalMentionsCount: typeof summary.external_mentions_count === 'number' ? summary.external_mentions_count : undefined,
        rawSourceSummary: Array.isArray(report.raw_source_summary) ? report.raw_source_summary : [],
        limitations: Array.isArray(report.limitations) ? report.limitations : [],
        evidence,
        location: { city: loc.city || undefined, region: loc.region || undefined, country: loc.country || undefined },
        gender: profile.gender_indicators?.value || undefined,
        ageRange: profile.age_range?.value || undefined,
        profConfidence: prof.confidence || undefined,
        possibleRoles: Array.isArray(prof.possible_roles) ? prof.possible_roles : [],
        roleSelfClaims: Array.isArray(prof.role_self_claims) ? prof.role_self_claims : [],
        possibleProducts: normalizeProducts(Array.isArray(prof.possible_products) ? prof.possible_products : []),
        tone: comm.tone || undefined,
        style: comm.style || undefined,
        topics: Array.isArray(comm.main_topics) ? comm.main_topics : [],
        linkedUsernames: Array.isArray(profile.linked_usernames) ? profile.linked_usernames : [],
        crossPlatform,
      }
    }
  }
  return null
}

/** The AI report sometimes fills a field with the literal string "unknown"
 * instead of omitting it — that's not a signal worth a row of its own, so
 * fields like Gender/Age range/Confidence are dropped entirely rather than
 * rendered as "Unknown". */
function isUnknown(v?: string): boolean {
  return !!v && v.trim().toLowerCase() === 'unknown'
}

function formatUtc(v: number): string {
  const ms = v > 1e12 ? v : v * 1000
  const d = new Date(ms)
  if (Number.isNaN(d.getTime())) return ''
  const pad = (n: number) => String(n).padStart(2, '0')
  return `${pad(d.getDate())}.${pad(d.getMonth() + 1)}.${d.getFullYear()} ${pad(d.getHours())}:${pad(d.getMinutes())}`
}

function confidenceTone(level?: string): FlagTone {
  const k = String(level || '').toLowerCase()
  if (k === 'high') return 'ok'
  if (k === 'medium') return 'conflict'
  if (k === 'low') return 'bad'
  return 'variant'
}

/** One card per evidence item — source badge, confidence flag, inference
 * type, date, the quoted excerpt, and a link out to where it came from. */
function EvidenceList({ items }: { items: EvidenceItem[] }) {
  const tk = useTokens()
  const { visible, open, setOpen, hiddenCount } = useShowMore(items, 6)

  return (
    <>
      {visible.map((ev, i) => (
        <Box key={i} sx={{ pt: i ? '10px' : 0, mt: i ? '10px' : 0, borderTop: i ? `1px solid ${tk.border}` : 'none' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
            {ev.sourceType && <SourceBadge source={ev.sourceType} />}
            {ev.confidenceImpact && <Flag tone={confidenceTone(ev.confidenceImpact)} label={`${sentence(ev.confidenceImpact)} confidence`} />}
            {ev.inferenceType && <Typography sx={{ fontSize: 10.5, color: tk.text3 }}>{sentence(ev.inferenceType.replace(/_/g, ' '))}</Typography>}
            {ev.createdUtc !== undefined && (
              <Typography sx={{ fontSize: 10.5, color: tk.text3, ml: 'auto' }}>{formatUtc(ev.createdUtc)}</Typography>
            )}
          </Box>
          {ev.quote && (
            <Typography sx={{ fontSize: 12.5, color: tk.text, mt: '6px', lineHeight: 1.5 }}>&ldquo;{ev.quote}&rdquo;</Typography>
          )}
          {ev.sourceUrl && (
            <Box
              component="a"
              href={ev.sourceUrl}
              target="_blank"
              rel="noopener noreferrer"
              sx={{ fontSize: 10.5, color: '#2D89F4', textDecoration: 'none', mt: '5px', display: 'inline-block', '&:hover': { textDecoration: 'underline' } }}
            >
              Source ↗
            </Box>
          )}
        </Box>
      ))}
      <ShowMoreButton open={open} hiddenCount={hiddenCount} onClick={() => setOpen((o) => !o)} />
    </>
  )
}

function CrossPlatformList({ items }: { items: CrossPlatformItem[] }) {
  const tk = useTokens()
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
      {items.map((c, i) => (
        <Box
          key={i}
          sx={{
            display: 'flex', alignItems: 'flex-start', gap: '10px',
            pb: i < items.length - 1 ? '10px' : 0, borderBottom: i < items.length - 1 ? `1px solid ${tk.border}` : 'none',
          }}
        >
          {c.platform && <SourceBadge source={c.platform} />}
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap' }}>
              <Typography sx={{ fontSize: 12.5, fontWeight: 700, color: tk.text }}>{c.username || c.platform || 'Unknown'}</Typography>
              {c.confidenceLabel && <Flag tone="primary" label={c.confidenceLabel} />}
              {c.matchScore !== undefined && <Typography sx={{ fontSize: 10.5, color: tk.text3 }}>{Math.round(c.matchScore * 100)}% match</Typography>}
            </Box>
            {c.assessment && <Typography sx={{ fontSize: 11.5, color: tk.text2, mt: '3px', lineHeight: 1.4 }}>{c.assessment}</Typography>}
            {c.url && (
              <Box
                component="a"
                href={c.url}
                target="_blank"
                rel="noopener noreferrer"
                sx={{ fontSize: 10.5, color: '#2D89F4', textDecoration: 'none', mt: '4px', display: 'inline-block', wordBreak: 'break-all', '&:hover': { textDecoration: 'underline' } }}
              >
                {c.url}
              </Box>
            )}
          </Box>
        </Box>
      ))}
    </Box>
  )
}

/** One Reddit post: subreddit/date/score header, title+body, image gallery
 * (click-to-enlarge via usePhotoLightbox), source link, and a capped list
 * of top comments. */
function PostRow({ post }: { post: RedditPost }) {
  const tk = useTokens()
  const { openAt, lightbox } = usePhotoLightbox(post.images)
  const { visible, open, setOpen, hiddenCount } = useShowMore(post.comments, 3)

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
        {post.subreddit && <SourceBadge source={post.subreddit} plain />}
        {post.subreddit && <Typography sx={{ fontSize: 11.5, fontWeight: 700, color: tk.text }}>r/{post.subreddit}</Typography>}
        {post.createdUtc !== undefined && <Typography sx={{ fontSize: 10.5, color: tk.text3 }}>{formatUtc(post.createdUtc)}</Typography>}
        <Box sx={{ display: 'flex', gap: '10px', ml: 'auto' }}>
          {post.score !== undefined && <Typography sx={{ fontSize: 10.5, color: tk.text3 }}>▲ {post.score}</Typography>}
          {post.numComments !== undefined && <Typography sx={{ fontSize: 10.5, color: tk.text3 }}>{post.numComments} comments</Typography>}
        </Box>
      </Box>

      {/* `title` and `text` are the same string for this feed's PostItem
       * rows (title is just the post title, text mirrors it) — showing
       * both duplicated the text, so only `text` renders here. */}
      {post.text && <Typography sx={{ fontSize: 13, fontWeight: 700, color: tk.text, mt: '6px' }}>{post.text}</Typography>}

      {post.images.length > 0 && (
        <Box sx={{ display: 'flex', gap: '6px', mt: '8px', flexWrap: 'wrap' }}>
          {post.images.map((img, i) => (
            <Box key={i} sx={{ width: 84, height: 84, borderRadius: '8px', overflow: 'hidden', flex: 'none' }}>
              <Photo value={img} onClick={() => openAt(i)} />
            </Box>
          ))}
        </Box>
      )}
      {lightbox}

      {(post.postUrl || post.url) && (
        <Box
          component="a"
          href={post.postUrl || post.url}
          target="_blank"
          rel="noopener noreferrer"
          sx={{ fontSize: 10.5, color: '#2D89F4', textDecoration: 'none', mt: '6px', display: 'inline-block', '&:hover': { textDecoration: 'underline' } }}
        >
          Source ↗
        </Box>
      )}

      {post.comments.length > 0 && (
        <Box sx={{ mt: '10px' }}>
          <Sech count={post.comments.length}>Comments</Sech>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
            {visible.map((c, i) => (
              <Typography key={i} sx={{ fontSize: 11.5, color: tk.text2 }}>
                {c.author && <Box component="span" sx={{ fontWeight: 700, color: tk.text, mr: '5px' }}>{c.author}</Box>}
                {c.text}
                {c.score !== undefined && <Box component="span" sx={{ color: tk.text3, ml: '6px', fontSize: 10.5 }}>({c.score})</Box>}
              </Typography>
            ))}
          </Box>
          <ShowMoreButton open={open} hiddenCount={hiddenCount} onClick={() => setOpen((o) => !o)} />
        </Box>
      )}
    </Box>
  )
}

/** The raw Reddit account record (karma, account age, verified/employee
 * badges, avatar) — separate card from the AI report's "Profile", since
 * it comes from a different part of the payload (`profileData.collection.
 * profile`) and can be present even when the AI report isn't. */
function RedditAccountCard({ meta }: { meta: RedditAccountMeta }) {
  const tk = useTokens()
  return (
    <ResCard title="Reddit account">
      <Box sx={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
        {meta.iconImg && (
          <Box sx={{ width: 48, height: 48, borderRadius: '50%', overflow: 'hidden', flex: 'none' }}>
            <Photo value={meta.iconImg} />
          </Box>
        )}
        <Box sx={{ minWidth: 0 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap' }}>
            <Typography sx={{ fontSize: 14, fontWeight: 700, color: tk.text }}>
              {meta.displayName || meta.username || 'Unknown'}
            </Typography>
            {meta.verified && <Flag tone="ok" label="Verified" />}
            {meta.isEmployee && <Flag tone="primary" label="Reddit employee" />}
          </Box>
          {meta.username && <Typography sx={{ fontSize: 11.5, color: tk.text3 }}>u/{meta.username}</Typography>}
        </Box>
      </Box>

      <Box sx={{ mt: '12px' }}>
        <Stats
          items={[
            { label: 'Comment karma', value: meta.commentKarma ?? '—' },
            { label: 'Link karma', value: meta.linkKarma ?? '—' },
            { label: 'Total karma', value: meta.totalKarma ?? '—' },
            { label: 'Account created', value: meta.createdUtc !== undefined ? formatUtc(meta.createdUtc) : '—' },
          ]}
        />
      </Box>

      {meta.description && (
        <Typography sx={{ fontSize: 12, color: tk.text2, mt: '10px', lineHeight: 1.5 }}>{meta.description}</Typography>
      )}
    </ResCard>
  )
}

function PostsList({ posts }: { posts: RedditPost[] }) {
  const tk = useTokens()
  const { visible, open, setOpen, hiddenCount } = useShowMore(posts, 5)
  return (
    <>
      {visible.map((p, i) => (
        <Box
          key={p.id}
          sx={{ pb: '14px', mb: '14px', borderBottom: i < visible.length - 1 || hiddenCount > 0 || open ? `1px solid ${tk.border}` : 'none' }}
        >
          <PostRow post={p} />
        </Box>
      ))}
      <ShowMoreButton open={open} hiddenCount={hiddenCount} onClick={() => setOpen((o) => !o)} />
    </>
  )
}

export function RedditTemplate({ apiData }: TemplateProps) {
  const tk = useTokens()
  const r = React.useMemo(() => extract(apiData), [apiData])
  const posts = React.useMemo(() => extractRedditPosts(apiData), [apiData])
  const accountMeta = React.useMemo(() => extractRedditAccountMeta(apiData), [apiData])

  if (!r && posts.length === 0 && !accountMeta) {
    return <NoData title="No Reddit report returned" hint="The report generator produced no output for this profile." />
  }

  if (!r) {
    return (
      <>
        {accountMeta && <RedditAccountCard meta={accountMeta} />}
        {posts.length > 0 && (
          <ResCard title="Posts" count={posts.length}>
            <PostsList posts={posts} />
          </ResCard>
        )}
        <RawCard data={apiData} />
      </>
    )
  }

  const locationText = [r.location.city, r.location.region, r.location.country].filter(Boolean).join(', ')
  const addresses: ValueRow[] = locationText ? [{ value: locationText }] : []

  return (
    <>
      <ResCard title="Key facts">
        <Stats
          items={[
            { label: 'Status', value: r.status ? sentence(r.status) : '—', tone: r.status === 'completed' ? 'ok' : undefined },
            { label: 'Analysis depth', value: r.analysisDepth ? sentence(r.analysisDepth) : '—' },
            { label: 'Posts', value: r.postsCount ?? '—' },
            { label: 'Comments', value: r.commentsCount ?? '—' },
            { label: 'External mentions', value: r.externalMentionsCount ?? '—' },
          ]}
        />
      </ResCard>

      {accountMeta && <RedditAccountCard meta={accountMeta} />}

      {(() => {
        const hasProfessional =
          !isUnknown(r.profConfidence) || r.possibleRoles.length > 0 || r.roleSelfClaims.length > 0 || r.possibleProducts.length > 0
        const hasCommunication = !isUnknown(r.tone) || !isUnknown(r.style) || r.topics.length > 0

        /**
         * Same 3-column masonry approach as PersonTemplate.tsx's own Map
         * card: when there's a location, the Map is pinned to a 2-column ×
         * 2-row block (`gridColumn`/`gridRow` below) and everything else —
         * Profile, and Professional indicators / Communication profile when
         * present — auto-flows into whatever's left, starting with column 1
         * of those same two rows. `composed: true` skips the generic
         * `fill`-clone for the Map, since `MapCard` already renders its own
         * full `ResCard` (title, Full screen/Show live map buttons).
         */
        type MidCard = {
          key: string
          node: React.ReactElement
          composed?: boolean
          gridColumn?: string
          gridRow?: string
          height?: number
        }
        const midCards: MidCard[] = []

        midCards.push({
          key: 'profile',
          node: (
            <ResCard title="Profile">
              <FieldTable
                rows={[
                  ['Username', r.username || '—'],
                  ['Location', locationText || 'not returned'],
                  ...(!isUnknown(r.gender) ? [['Gender', r.gender ? sentence(r.gender) : 'not returned'] as [string, React.ReactNode]] : []),
                  ...(!isUnknown(r.ageRange) ? [['Age range', r.ageRange || 'not returned'] as [string, React.ReactNode]] : []),
                  ...(!isUnknown(r.profConfidence) ? [['Profile confidence', r.profConfidence ? sentence(r.profConfidence) : '—'] as [string, React.ReactNode]] : []),
                ]}
              />
              {r.linkedUsernames.length > 0 && (
                <Box sx={{ mt: '12px' }}>
                  <Sech count={r.linkedUsernames.length}>Linked usernames</Sech>
                  <Chips items={r.linkedUsernames} />
                </Box>
              )}
            </ResCard>
          ),
        })

        if (hasProfessional) {
          midCards.push({
            key: 'professional',
            node: (
              <ResCard title="Professional indicators">
                {!isUnknown(r.profConfidence) && (
                  <FieldTable rows={[['Confidence', r.profConfidence ? sentence(r.profConfidence) : '—']]} />
                )}
                {r.possibleRoles.length > 0 && (
                  <Box sx={{ mt: '12px' }}>
                    <Sech count={r.possibleRoles.length}>Possible roles</Sech>
                    <Chips items={r.possibleRoles} />
                  </Box>
                )}
                {r.roleSelfClaims.length > 0 && (
                  <Box sx={{ mt: '12px' }}>
                    <Sech count={r.roleSelfClaims.length}>Self claims</Sech>
                    <ValueList rows={r.roleSelfClaims.map((v) => ({ value: v }))} cap={5} />
                  </Box>
                )}
                {r.possibleProducts.length > 0 && (
                  <Box sx={{ mt: '12px' }}>
                    <Sech count={r.possibleProducts.length}>Possible products</Sech>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: '9px' }}>
                      {r.possibleProducts.map((p, i) => (
                        <Box key={i}>
                          <Typography component="span" sx={{ fontSize: 12.5, fontWeight: 700, color: tk.text }}>
                            {p.name || 'Unnamed product'}
                          </Typography>
                          {p.confidence && (
                            <Typography component="span" sx={{ fontSize: 10.5, color: tk.text3, ml: '6px' }}>({sentence(p.confidence)})</Typography>
                          )}
                          {p.quote && <Typography sx={{ fontSize: 11.5, color: tk.text3, mt: '2px', lineHeight: 1.4 }}>&ldquo;{p.quote}&rdquo;</Typography>}
                        </Box>
                      ))}
                    </Box>
                  </Box>
                )}
              </ResCard>
            ),
          })
        }

        if (hasCommunication) {
          midCards.push({
            key: 'communication',
            node: (
              <ResCard title="Communication profile">
                {(!isUnknown(r.tone) || !isUnknown(r.style)) && (
                  <FieldTable
                    rows={[
                      ...(!isUnknown(r.tone) ? [['Tone', r.tone ? sentence(r.tone) : 'not returned'] as [string, React.ReactNode]] : []),
                      ...(!isUnknown(r.style) ? [['Style', r.style ? sentence(r.style) : 'not returned'] as [string, React.ReactNode]] : []),
                    ]}
                  />
                )}
                {r.topics.length > 0 && (
                  <Box sx={{ mt: '12px' }}>
                    <Sech count={r.topics.length}>Main topics</Sech>
                    <Chips items={r.topics} />
                  </Box>
                )}
              </ResCard>
            ),
          })
        }

        if (hasMapPins([], addresses)) {
          // Pinned first (`unshift`, not `push`) — CSS grid auto-placement
          // walks cards in source order, so this has to claim its 2×2 block
          // before Profile/Professional/Communication can auto-flow into it.
          midCards.unshift({
            key: 'map',
            composed: true,
            gridColumn: '2 / 4',
            gridRow: '1 / 3',
            height: MID_TILE_HEIGHT * 2 + MID_COLS_GAP,
            node: <MapCard addressCount={addresses.length} mapPoints={[]} addresses={addresses} />,
          })
        }

        // Ergonomics case (same rule PersonTemplate.tsx uses): the pinned
        // Map is 2 rows tall, but with only ONE other card to share the grid
        // with, that lone card would sit as a normal 1-row tile in column 1
        // next to a panel twice its height. Stretch it to match instead of
        // leaving the rest of that column empty.
        if (midCards.length === 2 && midCards[0].gridColumn) {
          midCards[1].gridRow = '1 / 3'
          midCards[1].height = MID_TILE_HEIGHT * 2 + MID_COLS_GAP
        }

        // Simulates the browser's own grid auto-placement (row-flow,
        // non-dense) to find how much width is left in the LAST card's row,
        // so that card can be stretched to fill it instead of leaving a gap.
        // While the pinned Map is on screen it reserves 2 of 3 columns for
        // rows 1–2, so only 1 column is actually free there for auto-flowed
        // cards — row 3 onward (if it's ever reached) is the normal
        // full-width 3-column grid.
        const hasPinnedCard = midCards.some((c) => !!c.gridColumn)
        const rowCapacity = (rowIdx: number) => (hasPinnedCard && rowIdx < 2 ? 1 : 3)

        const lastIndex = midCards.length - 1
        let row = 0
        let col = 0
        for (let i = 0; i < lastIndex; i++) {
          if (midCards[i].gridColumn) continue
          if (col + 1 > rowCapacity(row)) { row += 1; col = 0 }
          col += 1
          if (col >= rowCapacity(row)) { row += 1; col = 0 }
        }
        const lastSpan = col === 0 ? rowCapacity(row) : rowCapacity(row) - col

        return (
          <Cols n={3} align="start">
            {midCards.map((c, i) => {
              const isLast = i === lastIndex
              const span = isLast ? lastSpan : 1
              const content = c.composed ? c.node : React.cloneElement(c.node, { fill: true })
              const mobileOverride = (c.gridColumn || c.gridRow)
                ? { gridColumn: '1 / -1', gridRow: 'auto', height: MID_TILE_HEIGHT }
                : (isLast ? { gridColumn: '1 / -1' } : undefined)
              return (
                <Box
                  key={c.key}
                  sx={{
                    height: c.height ?? MID_TILE_HEIGHT,
                    ...(c.gridColumn ? { gridColumn: c.gridColumn } : (span > 1 ? { gridColumn: `span ${span}` } : {})),
                    ...(c.gridRow ? { gridRow: c.gridRow } : {}),
                    ...(mobileOverride ? { '@media (max-width:1080px)': mobileOverride } : {}),
                  }}
                >
                  {content}
                </Box>
              )
            })}
          </Cols>
        )
      })()}

      {r.evidence.length > 0 && (
        <ResCard title="Evidence" count={r.evidence.length}>
          <EvidenceList items={r.evidence} />
        </ResCard>
      )}

      {r.crossPlatform.length > 0 && (
        <ResCard title="Cross-platform matches" count={r.crossPlatform.length}>
          <CrossPlatformList items={r.crossPlatform} />
        </ResCard>
      )}

      {(r.rawSourceSummary.length > 0 || r.limitations.length > 0) && (
        <Cols n={2}>
          <Stack>
            {r.rawSourceSummary.length > 0 ? (
              <ResCard title="Summary">
                <ValueList rows={r.rawSourceSummary.map((v) => ({ value: v }))} cap={20} />
              </ResCard>
            ) : (
              <Box />
            )}
          </Stack>
          <Stack>
            {r.limitations.length > 0 ? (
              <ResCard title="Limitations">
                <ValueList rows={r.limitations.map((v) => ({ value: v }))} cap={20} />
              </ResCard>
            ) : (
              <Box />
            )}
          </Stack>
        </Cols>
      )}

      {posts.length > 0 && (
        <ResCard title="Posts" count={posts.length}>
          <PostsList posts={posts} />
        </ResCard>
      )}

      <RawCard data={apiData} />
    </>
  )
}
